import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { OwnerFilterBar } from '../components/OwnerFilterBar.tsx';
import { OwnerPicker } from '../components/OwnerPicker.tsx';
import { filterByOwner, type OwnerFilterCriteria } from '../lib/ownerFilter.ts';
import { companyInfoLabel } from '../lib/companyInfo.ts';
import { teamRegionNames, useTeamRegions } from '../lib/teamRegions.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';
import type { Company, Asset } from '../lib/types.ts';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function assetName(v: Asset): string {
  const parts = [v.year ? String(v.year) : '', v.make ?? '', v.model ?? ''].filter(Boolean);
  return parts.join(' ') || v.registration_plate || 'Asset';
}

export function AdminAssets() {
  const [creating, setCreating] = useState(false);
  const [criteria, setCriteria] = useState<OwnerFilterCriteria>({
    ownerIds: [],
    statuses: [],
    query: '',
  });

  const { activeTeamRegion } = useAdminAuth();
  const assetsQuery = useQuery({
    queryKey: ['assets', { region: activeTeamRegion }],
    queryFn: () =>
      adminApi.get<{ assets: Asset[] }>(
        activeTeamRegion
          ? `/api/assets?team_region=${encodeURIComponent(activeTeamRegion)}`
          : '/api/assets',
      ),
  });
  const companiesQuery = useQuery({
    queryKey: ['companies', { region: activeTeamRegion }],
    queryFn: () =>
      adminApi.get<{ companies: Company[] }>(
        activeTeamRegion
          ? `/api/companies?team_region=${encodeURIComponent(activeTeamRegion)}`
          : '/api/companies',
      ),
  });

  const assets = assetsQuery.data?.assets ?? [];
  const companies = companiesQuery.data?.companies ?? [];
  const filtered = useMemo(
    () =>
      filterByOwner(
        assets,
        criteria,
        (a) => `${a.make ?? ''} ${a.model ?? ''} ${a.registration_plate ?? ''}`,
      ),
    [assets, criteria],
  );

  if (assetsQuery.isLoading) return <CenteredSpinner />;
  if (assetsQuery.error) return <ErrorMessage error={assetsQuery.error} />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Assets</h1>
          <p className="mt-1 text-sm text-slate-500">
            Assets owned by your operating company and by each contractor company. Track details,
            insurance and registration renewals, photos, and run audits.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New asset</Btn>
      </div>

      <OwnerFilterBar
        criteria={criteria}
        onChange={setCriteria}
        companies={companies}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search make, model or plate…"
      />

      {filtered.length === 0 ? (
        <EmptyState
          title="No assets match"
          description="Adjust the filters, or add an asset to manage it and run audits."
          action={<Btn onClick={() => setCreating(true)}>+ New asset</Btn>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Asset</th>
                <th className="px-4 py-3">Plate</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/assets/${v.id}`}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {assetName(v)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{v.registration_plate ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{v.asset_type ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {v.company_id ? (v.company?.company_name ?? '—') : 'Operating company'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        v.compliance_status === 'compliant'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-orange-50 text-orange-700'
                      }`}
                    >
                      {v.compliance_status === 'compliant' ? 'Compliant' : 'Not compliant'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateAssetModal companies={companies} onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateAssetModal({ companies, onClose }: { companies: Company[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const teamRegions = teamRegionNames(useTeamRegions().data);
  const [form, setForm] = useState({
    company_id: '', // '' = Operating company (admin-owned)
    make: '',
    model: '',
    year: '',
    registration_plate: '',
    asset_type: '',
    team_region: '',
  });
  const [error, setError] = useState<unknown>(null);
  // team_region is only meaningful for admin-owned assets (operating company);
  // contractor assets inherit scope from their company (the API nulls it either
  // way).
  const isAdminOwned = form.company_id === '';

  const create = useMutation({
    mutationFn: () =>
      adminApi.post('/api/assets', {
        company_id: form.company_id || undefined,
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        year: form.year ? Number(form.year) : null,
        registration_plate: form.registration_plate.trim() || null,
        asset_type: form.asset_type.trim() || null,
        team_region: isAdminOwned ? form.team_region || null : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="New asset"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => create.mutate()} loading={create.isPending}>
            Create
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <OwnerPicker
          value={form.company_id}
          onChange={(company_id) => setForm({ ...form, company_id })}
          companies={companies}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Make">
            <TextInput value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
          </Field>
          <Field label="Model">
            <TextInput value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </Field>
          <Field label="Year">
            <TextInput
              type="number"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
            />
          </Field>
          <Field label="Registration plate">
            <TextInput
              value={form.registration_plate}
              onChange={(e) => setForm({ ...form, registration_plate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Type" hint="e.g. Van, Truck, Car">
          <TextInput
            value={form.asset_type}
            onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
          />
        </Field>
        {isAdminOwned && (
          <Field
            label={companyInfoLabel(branding, 'teamRegion')}
            hint={
              teamRegions.length === 0
                ? 'Add team regions in Settings to populate this dropdown.'
                : undefined
            }
          >
            <Select
              value={form.team_region}
              onChange={(e) => setForm({ ...form, team_region: e.target.value })}
              disabled={teamRegions.length === 0}
            >
              <option value="">— none —</option>
              {teamRegions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
